import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetCallsQuery, useGetConversationsQuery } from "@/store/rest_api";
import { getDisplayName } from "@/components/pages/messages/ConversationListItem";
import { getInitialsFromFullName } from "@/util/string";
import type Call from "@/model/interface/Call";
import { CallDirection, CallStatus } from "@/model/interface/Call";

interface ActivityItem {
  key: string;
  title: string;
  meta: string;
  timestamp: string;
  dotClassName?: string;
  dotColor?: string;
  onClick: () => void;
}

const callTitle = (call: Call): string => {
  const name = [call.contact?.firstName, call.contact?.lastName].filter(Boolean).join(" ");
  return name || call.phoneNumber;
};

const callStatusLabel: Record<CallStatus, string> = {
  [CallStatus.INITIATED]: "Initiated",
  [CallStatus.RINGING]: "Ringing",
  [CallStatus.IN_PROGRESS]: "In progress",
  [CallStatus.COMPLETED]: "Completed",
  [CallStatus.FAILED]: "Failed",
  [CallStatus.BUSY]: "Busy",
  [CallStatus.NO_ANSWER]: "No answer",
  [CallStatus.CANCELED]: "Canceled",
  [CallStatus.MISSED]: "Missed",
};

const callDotClassName = (status: CallStatus): string => {
  if (status === CallStatus.MISSED || status === CallStatus.NO_ANSWER || status === CallStatus.FAILED || status === CallStatus.BUSY) {
    return "bg-destructive";
  }
  if (status === CallStatus.COMPLETED) {
    return "bg-success";
  }
  return "bg-muted-foreground";
};

const relativeTime = (timestamp: string): string =>
  formatDistanceToNow(new Date(timestamp), { addSuffix: true });

const RecentActivityCard: React.FC = () => {
  const navigate = useNavigate();
  const { data: callsData, isLoading: callsLoading } = useGetCallsQuery({ size: 5 });
  const { data: conversationsData, isLoading: conversationsLoading } = useGetConversationsQuery({ size: 5 });

  const items = useMemo<ActivityItem[]>(() => {
    const calls = (callsData?.items ?? [])
      .filter((call) => call.createdAt)
      .map((call) => ({
        key: `call-${call.id}`,
        title: callTitle(call),
        meta: [
          call.direction === CallDirection.INBOUND ? "Inbound call" : "Outbound call",
          call.disposition?.name ?? callStatusLabel[call.status],
          relativeTime(call.createdAt!),
        ].join(" · "),
        timestamp: call.createdAt!,
        dotClassName: call.disposition?.color ? undefined : callDotClassName(call.status),
        dotColor: call.disposition?.color,
        onClick: () => navigate(`/calls?call=${call.id}`),
      }));
    const conversations = (conversationsData?.items ?? [])
      .filter((conversation) => conversation.lastMessageAt ?? conversation.createdAt)
      .map((conversation) => ({
        key: `conversation-${conversation.id}`,
        title: getDisplayName(conversation),
        meta: [
          "Message",
          conversation.lastMessagePreview,
          relativeTime((conversation.lastMessageAt ?? conversation.createdAt)!),
        ]
          .filter(Boolean)
          .join(" · "),
        timestamp: (conversation.lastMessageAt ?? conversation.createdAt)!,
        dotClassName: "bg-info",
        dotColor: undefined,
        onClick: () => navigate("/inbox"),
      }));
    return [...calls, ...conversations]
      .sort((first, second) => new Date(second.timestamp).getTime() - new Date(first.timestamp).getTime())
      .slice(0, 8);
  }, [callsData, conversationsData, navigate]);

  const isLoading = callsLoading || conversationsLoading;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Recent activity</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-10 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No recent activity yet.</p>
        ) : (
          <div className="divide-y">
            {items.map((item) => (
              <div
                key={item.key}
                role="button"
                tabIndex={0}
                onClick={item.onClick}
                className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2.5 hover:bg-muted/50"
              >
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarFallback className="bg-primary/10 text-xs text-primary">
                    {getInitialsFromFullName(item.title)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{item.title}</span>
                  <p className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
                    <span
                      className={`h-1.5 w-1.5 shrink-0 rounded-full ${item.dotClassName ?? ""}`}
                      style={item.dotColor ? { backgroundColor: item.dotColor } : undefined}
                    />
                    <span className="truncate">{item.meta}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentActivityCard;
